import React, { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useLogin, useRegister } from "../../services/AuthService";
import "./Login.css";

const LoginPage: React.FC = () => {
    const { user } = useAuth();
    const { login } = useLogin();
    const { register } = useRegister();
    const navigate = useNavigate();

    // true: login mode; false: registration mode.
    const [isLogin, setIsLogin] = useState(true);
    const [username, setUsername] = useState(""); // Only used in registration mode.
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState();

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setError(undefined);

        try {
            if (isLogin) {
                // Sign in logic.
                await login(email, password);
            } else {
                // Registration logic: require username, email, and password.
                await register(username, email, password);
            }
            navigate("/");
        } catch (err) {
            console.error(isLogin ? "Error during login:" : "Error during registration:", err);
            const { code, message } = err.response?.data || {};
            setError(message ? `${message} (${code})` : (isLogin ? "Login failed" : "Registration failed"));
        } finally {
            setIsLoading(false);
        }
    };

    const switchMode = () => {
        setIsLogin(!isLogin);
        setError(undefined);
    };

    if (user) {
        return <Navigate to="/" />;
    }

    return (
        <div className="login-container">
            <div className="login-card">
                <div className="login-header">
                    <div className="login-logo">
                        <span className="login-logo-icon"></span>
                    </div>
                    <h1 className="login-title">{isLogin ? "Sign in" : "Sign up"}</h1>
                    <p className="login-subtitle">
                        {isLogin ? "Welcome back" : "Create your account"}
                    </p>
                </div>

                {error && (
                    <marquee className="login-error" style={{ width: "auto", textAlign: "center" }}>
                        {error}
                    </marquee>
                )}

                <form className="login-form" onSubmit={handleSubmit}>
                    {!isLogin && (
                        <div className="login-form-group">
                            <div className="login-input-container">
                                <input
                                    className="login-input"
                                    type="text"
                                    id="username"
                                    value={username}
                                    onChange={(e) => setUsername(e.target.value)}
                                    required={!isLogin}
                                    placeholder=" "
                                    disabled={isLoading}
                                />
                                <label className="login-label" htmlFor="username">
                                    Username
                                </label>
                                <div className="login-input-icon user-icon"></div>
                            </div>
                        </div>
                    )}
                    <div className="login-form-group">
                        <div className="login-input-container">
                            <input
                                className="login-input"
                                type="email"
                                id="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                placeholder=" "
                                disabled={isLoading}
                            />
                            <label className="login-label" htmlFor="email">
                                Email
                            </label>
                            <div className="login-input-icon email-icon"></div>
                        </div>
                    </div>
                    <div className="login-form-group">
                        <div className="login-input-container">
                            <input
                                className="login-input"
                                type="password"
                                id="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                placeholder=" "
                                disabled={isLoading}
                            />
                            <label className="login-label" htmlFor="password">
                                Password
                            </label>
                            <div className="login-input-icon password-icon"></div>
                        </div>
                    </div>
                    <button
                        type="submit"
                        className={`login-button ${isLoading ? "login-button-loading" : ""}`}
                        disabled={isLoading}
                    >
                        {isLoading ? (
                            <span className="login-spinner"></span>
                        ) : isLogin ? (
                            "Sign in"
                        ) : (
                            "Create account"
                        )}
                    </button>
                </form>

                <div className="login-switch">
                    <button
                        className="login-switch-button"
                        onClick={switchMode}
                        type="button"
                        disabled={isLoading}
                    >
                        {isLogin
                            ? "Don't have an account? Sign up"
                            : "Already have an account? Sign in"}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default LoginPage;
